/* eslint-disable @typescript-eslint/no-non-null-assertion,@typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Callout, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentV2Resource, PermissionDescriptions, Permissions, ProjectedTeamReferenceDataItem, ProjectGroupResource, ProjectSummaryResource, Repository, SpaceResource, TenantResource, UserResource, } from "@octopusdeploy/octopus-server-client";
import { Permission, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { keyBy } from "lodash";
import { useCallback } from "react";
import * as React from "react";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { SimplePagingList } from "~/components/PagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Section } from "~/components/Section/Section";
import Select from "~/primitiveComponents/form/Select/Select";
import ProjectedTeamsList, { ProjectedTeamListItem } from "../ProjectedTeamsList/ProjectedTeamsList";
import { PermissionTable } from "./PermissionTable";
export interface TestPermissionsPageProps {
    loaderData: TestPermissionsPageLoaderData;
    queryParams: TestPermissionsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<TestPermissionsPageQueryParams>;
}
export interface TestPermissionsPageQueryParams {
    userId: string | undefined;
    spaceId: string | undefined;
    permissionScope: PermissionScope;
}
interface TestPermissionsPageLoaderData {
    users: UserResource[];
    permissionDescriptions: PermissionDescriptions;
}
interface UserPermissions {
    projectedTeamReferenceDataItems: ProjectedTeamReferenceDataItem[];
    spacePermissions: Permissions;
    systemPermissions: PermissionListItem[];
    isTeamsComplete: boolean;
    isPermissionsComplete: boolean;
    projectSummaries: {
        [ID: string]: ProjectSummaryResource;
    };
    environments: {
        [ID: string]: EnvironmentV2Resource;
    };
    tenants: {
        [ID: string]: TenantResource;
    };
    projectGroups: {
        [ID: string]: ProjectGroupResource;
    };
}
interface PermissionListItem {
    Id: string;
    Name: string;
}
const permissionScopeValues = ["System", "Space"] as const;
type PermissionScope = (typeof permissionScopeValues)[number];
class SystemPermissionList extends SimplePagingList<PermissionListItem> {
}
export async function testPermissionsPagePageLoader(repository: Repository) {
    const usersAsync = repository.Users.all();
    const permissionDescriptionsAsync = repository.PermissionDescriptions.all();
    return {
        users: await usersAsync,
        permissionDescriptions: await permissionDescriptionsAsync,
    };
}
export function TestPermissionsPage({ loaderData, queryParams, setQueryParams }: TestPermissionsPageProps) {
    const selectedUserId = queryParams.userId;
    const selectedUser = loaderData.users.find((u) => u.Id === selectedUserId || u.Username === selectedUserId);
    const { result } = useQuery(async (repository) => {
        if (!selectedUser)
            return null;
        return getAccessibleSpacesAndExportLink(repository, selectedUser, queryParams.spaceId);
    }, [queryParams.spaceId, selectedUser], "Get accessible spaces and permission export link for selected user");
    const exportPageAction: PrimaryPageAction | undefined = result
        ? {
            type: "navigate",
            label: "Export user permissions",
            external: true,
            path: result.exportUrl,
        }
        : undefined;
    const onUserChange = useCallback((userId: string | undefined) => {
        setQueryParams({ ...queryParams, userId, spaceId: undefined });
    }, [queryParams, setQueryParams]);
    const onSpaceChange = useCallback((spaceId: string | undefined) => {
        setQueryParams({ ...queryParams, spaceId });
    }, [queryParams, setQueryParams]);
    const onPermissionScopeChange = useCallback((permissionScope: PermissionScope) => {
        setQueryParams({ ...queryParams, permissionScope, spaceId: undefined });
    }, [queryParams, setQueryParams]);
    return (<PageContent header={{ title: "Test Permissions", primaryAction: exportPageAction }}>
            {loaderData.users && (<Section sectionHeader="User">
                    <Select label="Select a user" allowFilter={true} autoFocus={true} items={loaderData.users.map((u) => ({ value: u.Id, text: `${u.Username} (${u.DisplayName})` }))} onChange={onUserChange} value={selectedUserId}/>
                </Section>)}
            {selectedUser && (<Section sectionHeader="Permissions context">
                    <RadioButtonGroup accessibleName={"Permissions Context"} value={queryParams.permissionScope} onChange={onPermissionScopeChange}>
                        <RadioButton value={"System"} label="Show system permissions"/>
                        <RadioButton value={"Space"} label="Show permissions within a specific space"/>
                    </RadioButtonGroup>
                </Section>)}
            {selectedUser && result && queryParams.permissionScope === "Space" && (<UserSpacePermissions user={selectedUser} spaceId={queryParams.spaceId} onSpaceIdChange={onSpaceChange} accessibleSpaces={result.accessibleSpaces} permissionDescriptions={loaderData.permissionDescriptions}/>)}
            {selectedUser && result && queryParams.permissionScope === "System" && <UserSystemPermissions user={selectedUser} accessibleSpaces={result.accessibleSpaces}/>}
        </PageContent>);
}
function UserSystemPermissions({ user, accessibleSpaces }: {
    user: UserResource;
    accessibleSpaces: SpaceResource[];
}) {
    const { result } = useQuery(async (repository) => getSystemPermissions(repository, user), [user], "Get system permissions for selected user");
    if (!result)
        return null;
    return (<>
            <TeamPermissionRenderer user={user} isTeamsComplete={result.isTeamsComplete} projectedTeamReferenceDataItems={result.projectedTeamReferenceDataItems} spacesAccessibleBySelectedUser={accessibleSpaces}/>
            <Section sectionHeader={result.systemPermissions.length === 0 ? "No permissions" : "Permissions"}>
                {!result.isPermissionsComplete && <IncompletePermissionsInfoCallout displayName={user.DisplayName}/>}
                {result.systemPermissions.length > 0 && <SystemPermissionList items={result.systemPermissions} onRow={(p: PermissionListItem) => <div>{p.Name}</div>}/>}
            </Section>
        </>);
}
function UserSpacePermissions({ user, spaceId, onSpaceIdChange, accessibleSpaces, permissionDescriptions, }: {
    user: UserResource;
    spaceId: string | undefined;
    onSpaceIdChange: (spaceId: string | undefined) => void;
    accessibleSpaces: SpaceResource[];
    permissionDescriptions: PermissionDescriptions;
}) {
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const { result } = useQuery(async (repository) => {
        if (!spaceId)
            return null;
        return getUserPermissionForSpace(repository, spaceId, user, ephemeralEnvironmentsAreEnabled);
    }, [spaceId, user, ephemeralEnvironmentsAreEnabled], "Get space permissions for selected user");
    return (<>
            <div className={styles.userSelect}>
                <Select label="Select a space" allowFilter={true} autoFocus={true} items={accessibleSpaces.map((s) => ({ value: s.Id, text: `${s.Name} Space` }))} onChange={onSpaceIdChange} value={spaceId}/>
            </div>
            {result && (<>
                    <TeamPermissionRenderer user={user} isTeamsComplete={result.isTeamsComplete} projectedTeamReferenceDataItems={result.projectedTeamReferenceDataItems} spacesAccessibleBySelectedUser={accessibleSpaces}/>
                    <Section sectionHeader={Object.keys(result.spacePermissions).length === 0 ? "No permissions" : "Permissions"}>
                        {!result.isPermissionsComplete && <IncompletePermissionsInfoCallout displayName={user.DisplayName}/>}
                        <p>
                            <InternalLink to={links.userPage.generateUrl({ userId: user.Id })}>{user.Username || user.DisplayName}'s </InternalLink>
                            permissions are restricted to:
                        </p>
                        <PermissionTable showRestrictions={true} permissions={result.spacePermissions} permissionDescriptions={permissionDescriptions} projectSummaries={result.projectSummaries!} environments={result.environments!} tenants={result.tenants!} projectGroups={result.projectGroups!}/>
                    </Section>
                </>)}
        </>);
}
function TeamPermissionRenderer({ user, isTeamsComplete, projectedTeamReferenceDataItems, spacesAccessibleBySelectedUser, }: {
    user: UserResource;
    isTeamsComplete: boolean;
    projectedTeamReferenceDataItems: ProjectedTeamReferenceDataItem[];
    spacesAccessibleBySelectedUser?: SpaceResource[];
}) {
    return (<Section sectionHeader="Teams">
            The user <InternalLink to={links.userPage.generateUrl({ userId: user.Id })}>{user.Username} </InternalLink>
            is a member of the following teams:
            {!isTeamsComplete && (<Callout type={"information"} title={"Limited permissions"}>
                    Your permissions do not allow you to see all of the teams that '{user.DisplayName}' is a member of
                </Callout>)}
            <ProjectedTeamsList items={projectedTeamReferenceDataItems} onRowRedirectUrl={(teamReference) => links.teamPage.generateUrl({ teamId: teamReference.Id })} onRow={(team) => <ProjectedTeamListItem projectedTeam={team} spaceResource={spacesAccessibleBySelectedUser?.find((x) => team.SpaceId && x.Id === team.SpaceId)}/>}/>
        </Section>);
}
function IncompletePermissionsInfoCallout({ displayName }: {
    displayName: string;
}) {
    return (<Callout type={"information"} title={"Limited permissions"}>
            Your permissions do not allow you to see all of the permissions granted to '{displayName}'
        </Callout>);
}
async function getAccessibleSpacesAndExportLink(repository: Repository, user: UserResource, spaceId: string | undefined) {
    const accessibleSpaces = await repository.Users.getSpaces(user);
    const systemLevelRepository = repository.forSystem();
    const userPermissions = await systemLevelRepository.UserPermissions.getPermissionsConfigurationForAllParitions(user, true);
    const exportUrl = spaceId ? repository.resolve(userPermissions.Links["Export"], { spaces: spaceId }) : repository.resolve(userPermissions.Links["Export"]);
    return {
        accessibleSpaces,
        exportUrl,
    };
}
async function getSystemPermissions(repository: Repository, user: UserResource): Promise<UserPermissions> {
    const systemLevelRepository = repository.forSystem();
    const userPermissions = await systemLevelRepository.UserPermissions.getPermissionsConfigurationForCurrentSpaceContext(user, true);
    return {
        projectedTeamReferenceDataItems: userPermissions.Teams,
        spacePermissions: userPermissions.SpacePermissions,
        systemPermissions: userPermissions.SystemPermissions.map((p: Permission) => ({ Id: p, Name: p })),
        isTeamsComplete: userPermissions.IsTeamsComplete,
        isPermissionsComplete: userPermissions.IsPermissionsComplete,
        projectSummaries: {},
        environments: {},
        projectGroups: {},
        tenants: {},
    };
}
async function getUserPermissionForSpace(repository: Repository, spaceId: string, selectedUser: UserResource, ephemeralEnvironmentsAreEnabled: boolean): Promise<UserPermissions> {
    const spaceLevelRepository = await repository.forSpace(spaceId);
    const userPermissions = await spaceLevelRepository.UserPermissions.getPermissionsConfigurationForCurrentSpaceContext(selectedUser, false);
    const tenants = isAllowed({ permission: Permission.TenantView }, { spaceId }) && isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? spaceLevelRepository.Tenants.all() : Promise.resolve([]);
    const projectSummaries = isAllowed({ permission: Permission.ProjectView }, { spaceId }) ? spaceLevelRepository.Projects.summaries() : Promise.resolve([]);
    const environments = isAllowed({ permission: Permission.EnvironmentView }, { spaceId }) ? getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, spaceLevelRepository) : Promise.resolve([]);
    const projectGroups = isAllowed({ permission: Permission.ProjectGroupView }, { spaceId }) ? spaceLevelRepository.ProjectGroups.all() : Promise.resolve([]);
    return {
        projectedTeamReferenceDataItems: userPermissions.Teams,
        spacePermissions: userPermissions.SpacePermissions,
        systemPermissions: userPermissions.SystemPermissions.map((p) => ({ Id: p, Name: p })),
        isTeamsComplete: userPermissions.IsTeamsComplete,
        isPermissionsComplete: userPermissions.IsPermissionsComplete,
        projectSummaries: keyBy(await projectSummaries, "Id"),
        environments: keyBy(await environments, "Id"),
        tenants: keyBy(await tenants, "Id"),
        projectGroups: keyBy(await projectGroups, "Id"),
    };
}
const styles = {
    userSelect: css({
        margin: `0 ${space[16]}`,
    }),
};
